import {getTestProps} from '@benchling/js-test-utils';
import {
  Button,
  createStyles,
  Divider,
  Flex,
  Group,
  Stack,
  Text,
  Tooltip,
  UnstyledButton,
  useMantineTheme,
} from '@mantine/core';
import {useDisclosure} from '@mantine/hooks';
import React from 'react';

import BenchlingConfig from 'BenchlingConfig';
import FileBrowserActions from 'file-browser/FileBrowserActions';
import {getAccessibleOrInaccessibleId, isInaccessible} from 'graphql-client/InaccessibleInfo';
import StorableChip from 'inventory/chips/StorableChip';
import {GetScratchPlateCreatedPlates_scratchPlate_ScratchPlateNode_createdPlates as ScratchPlateCreatedPlate} from 'notebook/integrations/plate-maps/scratch-plates/__generated/GetScratchPlateCreatedPlates';
import MiniPlateSummary, {
  MiniPlateSummaryFilledPosition,
} from 'notebook/integrations/plate-maps/scratch-plates/MiniPlateSummary';
import PlateMetadataFieldsModal from 'notebook/integrations/plate-maps/scratch-plates/PlateMetadataFieldsModal';
import {scratchPlateSchemaId} from 'notebook/integrations/plate-maps/scratch-plates/ScratchPlateDesignSummaryUtil';
import {SCRATCH_PLATE_VIEWER_MIN_HEIGHT} from 'notebook/integrations/plate-maps/scratch-plates/ScratchPlateViewConstants';
import ScratchPlateViewer from 'notebook/integrations/plate-maps/scratch-plates/ScratchPlateViewer';
import useFilledScratchPlateData from 'notebook/integrations/plate-maps/scratch-plates/useFilledScratchPlateData';
import {openCopyScratchPlateIdsModal} from 'samples/plate-maps/scratch-plates/CopyScratchPlateIdsModal';
import {openPutScratchPlateInInventoryModal} from 'samples/plate-maps/scratch-plates/PutScratchPlateInInventoryModal';
import {CollapsibleSidebar} from 'samples/plate-maps/sidebar/CollapsibleSidebar';
import {PlateSize} from 'samples/storables/StorableSchemaConstants';
import {FONT_WEIGHT_PROXIMA_SEMIBOLD, FONT_WEIGHT_PROXIMA_REGULAR} from 'styles/FontConstants';
import LegacyButton, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {ChipSize} from 'ui-library/chips/Chip';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import DropdownMenu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import Section from 'ui-library/menus/Section';

export type RecordsListItem =
  | {
      kind: 'plate';
      id: string;
      name: string | null;
      createdAt: string | null;
      inventoryPlateId: string | null;
      inventoryPlateInaccessible: boolean;
      width: number;
      height: number;
      // This record's own linked plate schema, if any — distinct from the design's schema, since a
      // record created before the design had a schema (or filled from a schema-less design) has none.
      // Optional so existing call sites/fixtures that don't care about it don't all need updating;
      // treated as null when omitted.
      plateSchemaId?: string | null;
      // `null` when the plate has no summary yet, so we render no preview at all.
      filledPositions: ReadonlyArray<MiniPlateSummaryFilledPosition> | null;
    }
  | {
      kind: 'inaccessible';
      id: string;
      invalidId: string;
    };

export function buildScratchPlateRecordsItems(
  createdPlates: Array<ScratchPlateCreatedPlate>
): Array<RecordsListItem> {
  // Backend returns created plates newest-first; preserve that order.
  return createdPlates.flatMap((createdPlate): Array<RecordsListItem> => {
    if (createdPlate.__typename === 'ScratchPlateNode') {
      return [
        {
          kind: 'plate',
          id: createdPlate.id,
          name: createdPlate.name,
          createdAt: createdPlate.createdAt,
          inventoryPlateId:
            createdPlate.inventoryPlate != null
              ? getAccessibleOrInaccessibleId(createdPlate.inventoryPlate)
              : null,
          inventoryPlateInaccessible:
            createdPlate.inventoryPlate != null && isInaccessible(createdPlate.inventoryPlate),
          width: createdPlate.width,
          height: createdPlate.height,
          plateSchemaId: scratchPlateSchemaId(createdPlate),
          filledPositions: createdPlate.summary?.wellStates ?? null,
        },
      ];
    }
    if (isInaccessible(createdPlate)) {
      return [
        {
          kind: 'inaccessible',
          id: `inaccessible-${createdPlate.invalidId}`,
          invalidId: createdPlate.invalidId,
        },
      ];
    }
    return [];
  });
}

function RecordsDetailView({
  scratchPlateId,
  scratchPlateName,
  inventoryPlateId,
  inventoryPlateInaccessible,
  onPutInInventoryComplete,
}: {
  scratchPlateId: string;
  scratchPlateName: string | null;
  inventoryPlateId: string | null;
  inventoryPlateInaccessible: boolean;
  onPutInInventoryComplete?: () => void;
}): JSX.Element {
  const {classes} = useStyles();
  const theme = useMantineTheme();
  const {result, plate, plateSchema, plateFields} = useFilledScratchPlateData(scratchPlateId);
  const displayName = plate?.name ?? scratchPlateName ?? scratchPlateId;
  const [infoModalOpened, {open: openInfoModal, close: closeInfoModal}] = useDisclosure(false);

  function handlePutInInventory(): void {
    if (plate == null) {
      return;
    }
    openPutScratchPlateInInventoryModal({
      showSelectPlatesStep: false,
      plates: [
        {
          scratchPlateId,
          scratchPlateName: plate.name,
          plateSize: [plate.height, plate.width],
          plateSchemaId: scratchPlateSchemaId(plate),
          // This button only renders when the plate has no inventory plate yet.
          inventoryPlateId: null,
          // The design summary query doesn't fetch well states or createdAt.
          filledPositions: null,
          createdAt: null,
        },
      ],
      onCompleted: onPutInInventoryComplete,
    });
  }

  return (
    <Flex direction="column" h="100%" w="100%">
      <Group position="apart" align="flex-start" spacing={8}>
        <Text
          className={classes.plateViewTitle}
          {...getTestProps({component: 'ScratchPlateRecordsView', element: 'viewingPlateName'})}
        >
          Viewing
          <Text span={true} className={classes.plateName}>
            {` ${displayName}`}
          </Text>
        </Text>
        <Group spacing={8} noWrap={true}>
          <LegacyButton
            size={ButtonSize.SMALL}
            icon="info-circle"
            aria-label="View plate metadata"
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            onClick={openInfoModal}
          />
          {inventoryPlateId != null ? (
            <Tooltip
              label="You do not have access to this inventory plate"
              disabled={!inventoryPlateInaccessible}
            >
              <span>
                <Button
                  size="sm"
                  leftIcon={<BenchlingIcon icon="open" />}
                  variant="tertiary"
                  styles={{root: {color: theme.colors.gray[9]}}}
                  disabled={inventoryPlateInaccessible}
                  onClick={() => {
                    FileBrowserActions.openFile(inventoryPlateId);
                  }}
                  {...getTestProps({component: 'ScratchPlateRecordsView', element: 'openInInventoryButton'})}
                >
                  Open in inventory
                </Button>
              </span>
            </Tooltip>
          ) : (
            BenchlingConfig.ENABLE_SCRATCH_PLATES && (
              <Button
                size="sm"
                leftIcon={<BenchlingIcon icon="box-arrow-right" />}
                variant="tertiary"
                styles={{root: {color: theme.colors.gray[9]}}}
                disabled={plate == null}
                onClick={handlePutInInventory}
                {...getTestProps({component: 'ScratchPlateRecordsView', element: 'putInInventoryButton'})}
              >
                Put in inventory
              </Button>
            )
          )}
        </Group>
      </Group>
      <Flex direction="column" mih={0} sx={{flex: 1}} w="100%">
        <div className={classes.plateViewerWrapper}>
          <ScratchPlateViewer result={result} />
        </div>
      </Flex>
      <PlateMetadataFieldsModal
        opened={infoModalOpened}
        onClose={closeInfoModal}
        plateSchema={plateSchema}
        fields={plateFields ?? []}
      />
    </Flex>
  );
}

interface ScratchPlateRecordsViewProps {
  recordsItems: Array<RecordsListItem>;
  selectedPlateId: string | null;
  onSelectPlateId: (id: string) => void;
  viewToggle?: React.ReactNode;
  /** When provided, renders a "Fill additional plates" button below the list. */
  onFillAdditionalPlates?: () => void;
  fillAdditionalPlatesDisabled?: boolean;
  /**
   * Dimensions of the design these plates were filled from. All created plates share them, so this
   * seeds the bulk put-in-inventory modal (each plate's own plateSchemaId is used for the schema).
   */
  designPlateSize: PlateSize;
  onPutInInventoryComplete?: () => void;
}

export default function ScratchPlateRecordsView({
  recordsItems,
  selectedPlateId,
  onSelectPlateId,
  viewToggle,
  onFillAdditionalPlates,
  fillAdditionalPlatesDisabled = false,
  designPlateSize,
  onPutInInventoryComplete,
}: ScratchPlateRecordsViewProps): JSX.Element {
  const {classes, cx} = useStyles();
  const selectedPlateItem =
    selectedPlateId == null
      ? null
      : (recordsItems.find(
          (item): item is Extract<RecordsListItem, {kind: 'plate'}> =>
            item.kind === 'plate' && item.id === selectedPlateId
        ) ?? null);

  const plateItems = recordsItems.filter(
    (item): item is Extract<RecordsListItem, {kind: 'plate'}> => item.kind === 'plate'
  );
  const hasNonInventoryPlates = plateItems.some((item) => item.inventoryPlateId == null);

  function handleBulkPutInInventory(): void {
    if (plateItems.length === 0) {
      return;
    }
    openPutScratchPlateInInventoryModal({
      plates: plateItems.map((item) => ({
        scratchPlateId: item.id,
        scratchPlateName: item.name,
        plateSize: designPlateSize,
        plateSchemaId: item.plateSchemaId ?? null,
        inventoryPlateId: item.inventoryPlateId,
        filledPositions: item.filledPositions,
        createdAt: item.createdAt,
      })),
      onCompleted: onPutInInventoryComplete,
    });
  }

  function handleOpenCopyPlateIdsModal(): void {
    if (plateItems.length === 0) {
      return;
    }
    openCopyScratchPlateIdsModal({
      plates: plateItems.map((item) => ({
        scratchPlateId: item.id,
        scratchPlateName: item.name,
        filledPositions: item.filledPositions,
        createdAt: item.createdAt,
      })),
      plateSize: designPlateSize,
    });
  }

  return (
    <Flex sx={{flex: 1}} miw={0}>
      <CollapsibleSidebar
        component="ScratchPlateRecordsSidebar"
        stickyHeader={viewToggle}
        disableScroll={true}
      >
        <Stack h="100%" spacing={8}>
          <Stack className={classes.recordsSidebarList} spacing={8}>
            {recordsItems.map((item) =>
              item.kind === 'plate' ? (
                <div
                  key={item.id}
                  className={cx(classes.recordsSidebarRow, {
                    [classes.recordsSidebarRowActive]: selectedPlateId === item.id,
                  })}
                >
                  <div className={classes.recordsSidebarRowControls}>
                    <div className={classes.recordsSidebarRowContent}>
                      <UnstyledButton
                        onClick={() => {
                          onSelectPlateId(item.id);
                        }}
                        aria-pressed={selectedPlateId === item.id}
                        className={classes.recordsSidebarSelectButton}
                      >
                        {item.filledPositions != null ? (
                          <div className={classes.recordsSidebarPreviewContent}>
                            <MiniPlateSummary
                              width={item.width}
                              height={item.height}
                              filledPositions={item.filledPositions}
                            />
                            {item.inventoryPlateId != null ? (
                              <StorableChip id={item.inventoryPlateId} size={ChipSize.SMALL} />
                            ) : (
                              <Text fw={FONT_WEIGHT_PROXIMA_SEMIBOLD}>{item.name ?? item.id}</Text>
                            )}
                          </div>
                        ) : (
                          <Text fw={FONT_WEIGHT_PROXIMA_SEMIBOLD}>{item.name ?? item.id}</Text>
                        )}
                      </UnstyledButton>
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  key={item.id}
                  className={cx(classes.recordsSidebarRow, classes.recordsSidebarRowDisabled)}
                >
                  <Stack spacing={2}>
                    <Text fw={FONT_WEIGHT_PROXIMA_SEMIBOLD}>Inaccessible plate</Text>
                    <Text className={classes.recordsSidebarRowMeta}>{item.invalidId}</Text>
                  </Stack>
                </div>
              )
            )}
          </Stack>
          {BenchlingConfig.ENABLE_SCRATCH_PLATES && (
            <div className={classes.footerContainer}>
              <span>
                <Dropdown
                  alwaysDropDown={true}
                  allowMenuToCloseDropdown={false}
                  menu={
                    <DropdownMenu>
                      <Section>
                        <Option
                          isDisabled={!hasNonInventoryPlates}
                          tooltipTitle={
                            hasNonInventoryPlates ? null : 'All plates have already been put in inventory'
                          }
                          onSelect={handleBulkPutInInventory}
                        >
                          Put plates in inventory
                        </Option>
                        {BenchlingConfig.PIPELINES_ENABLE_EXPERIMENTAL_SCRATCH_PLATE_STEPS && (
                          <Option onSelect={handleOpenCopyPlateIdsModal}>Copy plate IDs</Option>
                        )}
                      </Section>
                    </DropdownMenu>
                  }
                >
                  <Button
                    size="sm"
                    variant="tertiary"
                    fullWidth={true}
                    rightIcon={<BenchlingIcon icon="chevron-down" />}
                  >
                    Plate actions
                  </Button>
                </Dropdown>
              </span>

              {onFillAdditionalPlates != null && (
                <Button
                  size="sm"
                  leftIcon={<BenchlingIcon icon="fill-grid" />}
                  variant="tertiary"
                  fullWidth={true}
                  disabled={fillAdditionalPlatesDisabled}
                  onClick={onFillAdditionalPlates}
                >
                  Fill additional plates
                </Button>
              )}
            </div>
          )}
        </Stack>
      </CollapsibleSidebar>
      <Divider orientation="vertical" className={classes.recordsDivider} />
      <Flex direction="column" sx={{flex: 1}} miw={0} className={classes.recordsPlatePane}>
        {selectedPlateId == null ? (
          // Keeps the empty state centered when the parent has no fixed height.
          <Flex justify="center" align="center" mih={SCRATCH_PLATE_VIEWER_MIN_HEIGHT} h="100%">
            <Text className={classes.emptyDetail}>Select a plate to view details.</Text>
          </Flex>
        ) : (
          <RecordsDetailView
            key={selectedPlateId}
            scratchPlateId={selectedPlateId}
            scratchPlateName={selectedPlateItem?.name ?? null}
            inventoryPlateId={selectedPlateItem?.inventoryPlateId ?? null}
            inventoryPlateInaccessible={selectedPlateItem?.inventoryPlateInaccessible ?? false}
            onPutInInventoryComplete={onPutInInventoryComplete}
          />
        )}
      </Flex>
    </Flex>
  );
}

const useStyles = createStyles((theme) => ({
  recordsSidebarList: {
    flex: 1,
    overflowY: 'auto',
    minHeight: 0,
  },
  footerContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing.sm,
    paddingTop: theme.spacing.sm,
  },
  recordsDivider: {
    margin: `-${theme.spacing.md} ${theme.spacing.md} -${theme.spacing.md} 0`,
    borderColor: theme.colors.gray[3],
  },
  recordsPlatePane: {
    overflow: 'auto',
  },
  recordsSidebarRow: {
    width: '100%',
    border: `1px solid ${theme.colors.gray[3]}`,
    borderRadius: theme.radius.sm,
    background: theme.white,
    paddingBlock: theme.spacing.sm,
    paddingInline: theme.spacing.md,
  },
  recordsSidebarSelectButton: {
    flex: 1,
    minWidth: 0,
    textAlign: 'left',
  },
  recordsSidebarRowControls: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing.sm,
  },
  recordsSidebarRowContent: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing.sm,
  },
  recordsSidebarRowActive: {
    background: theme.colors.blue[0],
    borderColor: theme.colors.blue[4],
  },
  recordsSidebarPreviewContent: {
    display: 'grid',
    gridTemplateColumns: 'max-content minmax(0, 1fr)',
    alignItems: 'center',
    gap: theme.spacing.sm,
  },
  recordsSidebarRowDisabled: {
    cursor: 'not-allowed',
    opacity: 0.7,
  },
  recordsSidebarRowMeta: {
    color: theme.colors.gray[5],
    fontSize: theme.fontSizes.xs,
  },
  plateViewTitle: {
    color: theme.colors.gray[7],
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    fontSize: theme.fontSizes.md,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  plateName: {
    color: theme.colors.gray[7],
    fontWeight: FONT_WEIGHT_PROXIMA_SEMIBOLD,
    fontSize: theme.fontSizes.xl,
  },
  emptyDetail: {
    color: theme.colors.gray[5],
  },
  plateViewerWrapper: {
    width: '100%',
    height: '100%',
    // PlateMapView sets minWidth inline on the .plate-map SVG, so !important is
    // needed to let the embedded viewer shrink to fit the detail pane.
    '.plate-map': {
      minWidth: '0 !important',
      width: '100%',
    },
    '.plate-map-legend': {
      width: '100%',
    },
  },
}));
